import { Editor, Node, Path, Range, Transforms, Element } from 'slate';
import isHotkey from 'is-hotkey';
import { EMPHASIS_DEFINITIONS, HEADING_DEFINITIONS, LINK_DEFINITIONS, LIST_ITEM_DEFINITIONS, ORDERED_LIST_DEFINITIONS, PARAGRAPH_DEFINITIONS, QUOTE_DEFINITIONS, QUOTE_SOURCE_DEFINITIONS, QUOTE_TEXT_DEFINITIONS, SUMMARY_DEFINITIONS, TITLE_DEFINITIONS, UNORDERED_LIST_DEFINITIONS, } from './definitions';
const LIST_TYPES = [
    ORDERED_LIST_DEFINITIONS.type,
    UNORDERED_LIST_DEFINITIONS.type,
];
const defaultDisabledForBlockTypes = [
    EMPHASIS_DEFINITIONS.type,
    HEADING_DEFINITIONS.type,
    QUOTE_DEFINITIONS.type,
    SUMMARY_DEFINITIONS.type,
    TITLE_DEFINITIONS.type,
];
function isCharKey(event) {
    return (event.key.length === 1 && !(event.metaKey || event.ctrlKey || event.altKey));
}
function getNode({ editor, definitions }) {
    return editor.value.blocks.find((block) => !!block && block.type === definitions.type);
}
const inlineNormalizeHelper = (editor, error) => {
    if (error.code === 'child_object_invalid') {
        editor.unwrapInlineByKey(error.child.key);
    }
};
function hasPreviousSiblingWithContent(node, document) {
    const prevSibling = document.getPreviousSibling(node.key);
    if (!prevSibling)
        return false;
    if (prevSibling.text.length)
        return true;
    return hasPreviousSiblingWithContent(prevSibling, document);
}
const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
        Editor.removeMark(editor, format);
    }
    else {
        Editor.addMark(editor, format, true);
    }
};
const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};
const isBlockActive = (editor, format, data) => {
    const [match] = Editor.nodes(editor, {
        match: (n) => {
            if (data) {
                return (n.type === format && JSON.stringify(n.data) === JSON.stringify(data));
            }
            else {
                return n.type === format;
            }
        },
        mode: 'all',
    });
    return !!match;
};
export const isList = (format) => format === ORDERED_LIST_DEFINITIONS.type ||
    format === UNORDERED_LIST_DEFINITIONS.type;
const isQuote = (format) => format === QUOTE_DEFINITIONS.type;
const liftQuote = (editor, format, data) => {
    const grandParentPath = getGrandParentPath(editor);
    const quoteNode = Node.get(editor, grandParentPath);
    const quoteTextNode = Node.child(quoteNode, 0);
    const createAnEmptyNode = {
        type: format,
        children: quoteTextNode.children,
        at: grandParentPath,
    };
    Transforms.removeNodes(editor, {
        at: grandParentPath,
    });
    Transforms.insertNodes(editor, [createAnEmptyNode], {
        at: grandParentPath,
        select: true,
    });
};
const toggleBlock = (editor, format, data) => {
    const isActive = isBlockActive(editor, format, data);
    const isQuoteText = isBlockActive(editor, QUOTE_TEXT_DEFINITIONS.type);
    const isSourceText = isBlockActive(editor, QUOTE_SOURCE_DEFINITIONS.type);
    if (isActive) {
        // that means, we're in fact deactivating the option
        if (isList(format)) {
            // move the list-item up in the tree to the same level of un/ordered-list
            // notice that if the un/ordered-list becomes empty, Slate will remove it automatically
            Transforms.liftNodes(editor);
        }
        if (isQuote(format)) {
            liftQuote(editor, PARAGRAPH_DEFINITIONS.type);
            return;
        }
        // then set it as paragraph
        Transforms.setNodes(editor, {
            type: PARAGRAPH_DEFINITIONS.type,
        });
    }
    else {
        // activating the option
        if (isList(format)) {
            // clean up nodes
            Transforms.unwrapNodes(editor, {
                match: (n) => {
                    const nodeType = !Editor.isEditor(n) && Element.isElement(n) && n.type;
                    return !!(nodeType && LIST_TYPES.includes(nodeType));
                },
                split: true,
            });
            // set the node as list-item instead of un/ordered-list
            Transforms.setNodes(editor, {
                type: LIST_ITEM_DEFINITIONS.type,
                data,
            });
            // then create an un/ordered-list and "wrap" the converted node
            // i.e. put it inside of this new node
            Transforms.wrapNodes(editor, {
                type: format,
                children: [],
            });
        }
        else if (isQuote(format)) {
            Transforms.setNodes(editor, {
                type: QUOTE_TEXT_DEFINITIONS.type,
            });
            Transforms.wrapNodes(editor, {
                type: QUOTE_DEFINITIONS.type,
            });
        }
        else {
            // use case when transforms from headings to quote
            if (isSourceText || isQuoteText) {
                liftQuote(editor, format, data);
            }
            Transforms.setNodes(editor, {
                type: format,
                data,
            });
        }
    }
};
const onKeyDownMark = ({ mark, hotkey }) => (e, editor) => {
    if (isHotkey(hotkey, e) && !isDefaultDisabledBlockActive(editor)) {
        e.preventDefault();
        toggleMark(editor, mark);
    }
};
const isDefaultDisabledBlockActive = (editor) => defaultDisabledForBlockTypes.some((type) => isBlockActive(editor, type));
const isNodeEmpty = (element) => Node.string(element).length === 0;
// for now without nested support, i.e. it will only support un/ordered-list > list-item
const isLatestChildren = (editor, path) => editor.children[path[0]].children.length - 1 === path[1];
const createAnEmptyParagraph = () => ({
    type: PARAGRAPH_DEFINITIONS.type,
    children: [
        {
            text: '',
        },
    ],
});
const getParentPath = (editor) => {
    // for instance, [2,2,0], which means:
    // third element (after title & summary),
    // third element (listItem position), first text inside of it
    const path = editor.selection.anchor.path;
    return Path.parent(path); // in the previous example that'd mean [2,2]
};
// the node that points to that path, i.e. { type: 'listItem', children:[] }
const getParentNode = (editor) => Node.get(editor, getParentPath(editor));
const getGrandParentPath = (editor) => {
    const path = [...editor.selection.anchor.path];
    const parentPath = Path.parent(path);
    const grandParentPath = Path.parent(parentPath);
    return grandParentPath;
};
const getGrandParentNode = (editor) => Node.get(editor, getGrandParentPath(editor));
const checkIfExitBlockElement = ({ blockType }) => (event, editor) => {
    if (event.key === 'Enter') {
        const previousNode = getParentNode(editor);
        const previousNodeText = Node.string(previousNode);
        if (previousNodeText === '' && previousNode.type === blockType) {
            event.preventDefault();
            Transforms.removeNodes(editor);
            Transforms.insertNodes(editor, [createAnEmptyParagraph()]);
        }
    }
};
const checkIfShouldCloseListAndCreateAParagraph = () => (event, editor) => {
    if (event.key === 'Enter') {
        let parentPath = getParentPath(editor);
        const parentNode = getParentNode(editor);
        const parentText = Node.string(parentNode);
        const parentLinks = parentNode.children.filter((c) => c.type === LINK_DEFINITIONS.type);
        let lifted = false;
        // only block if previous is empty and is last bullet
        // notice that because links' text are nested
        // we need to check for list existence as well as checking the text
        if (parentText === '' &&
            parentLinks.length === 0 &&
            isLatestChildren(editor, parentPath)) {
            event.preventDefault(); // don't allow new bullet
            let path = editor.selection.anchor.path;
            if (
            // list with a single, empty list-item
            // un/ordered-list with a single list-item children
            // list-item with a single text children
            // list-item's empty text
            editor.children[path[0]].children.length === 1 &&
                editor.children[path[0]].children[0].children.length === 1 &&
                editor.children[path[0]].children[0].children[0].text === '') {
                Transforms.liftNodes(editor);
                lifted = true;
            }
            Transforms.removeNodes(editor); // remove the previously existent empty list-item
            let optionsToInsert = {}; // default position is at the end of document
            if (lifted && editor.children.length > 0) {
                parentPath = getParentPath(editor);
                // get again the value, because it changes when lifted
                // but only if has children, otherwise it will fail
            }
            if (editor.children.length > 0) {
                optionsToInsert = {
                    at: [parentPath[0] + 1], // the position after the previous element (i.e. the list)
                };
            }
            Transforms.insertNodes(editor, [createAnEmptyParagraph()], // the nodes to insert, always has to be an array
            optionsToInsert);
            Transforms.move(editor, { distance: 1 }); // move the cursor to the newly created paragraph
        }
    }
};
const fixTripleClickSelection = (editor) => {
    /* When users triple click, slate returns the selection as being
       from the first char of clicked block until the first char of next block.
       This reduces the selection to the latest char of the current block. */
    const { selection } = editor;
    if (selection &&
        selection.anchor.offset === 0 &&
        selection.focus.offset === 0 &&
        !Range.isCollapsed(selection)) {
        const point = Editor.before(editor, selection.focus);
        point && Transforms.setPoint(editor, point, { edge: 'focus' });
    }
};
export { createAnEmptyParagraph, checkIfExitBlockElement, checkIfShouldCloseListAndCreateAParagraph, fixTripleClickSelection, getGrandParentNode, getGrandParentPath, getNode, getParentNode, getParentPath, hasPreviousSiblingWithContent, inlineNormalizeHelper, isBlockActive, isCharKey, isMarkActive, isNodeEmpty, onKeyDownMark, toggleBlock, toggleMark, };
//# sourceMappingURL=helpers.js.map